 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Save, LogOut, Settings, RefreshCw, Map, Grid } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { PhaseProgressionSettings } from './settings/PhaseProgressionSettings';
import { MusicSettings } from './settings/MusicSettings';
import { ThemeSettings } from './settings/ThemeSettings';
import type { PhaseProgressionPreferences } from '@smartphone-tycoon/shared-core';
import { useDevModeStore } from '../../stores/devModeStore';
import { useNavigationModeStore, type NavigationMode } from '../../stores/navigationModeStore';
import { useSpatialStore } from '../../stores/spatialStore';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  onReturnToMenu: () => void;
  isSaving: boolean;
  saveSuccess: boolean;
  phasePreferences: PhaseProgressionPreferences;
  onPhasePreferenceChange: (phase: keyof PhaseProgressionPreferences, choice: string) => void;
  fireWarningEnabled?: boolean;
  onFireWarningChange?: (enabled: boolean) => void;
  onRestartTutorial?: () => void;
  audioSettings?: {
    musicEnabled: boolean;
    musicVolume: number;
    currentTrack: number;
  };
  onAudioSettingsChange?: (updates: Partial<{
    musicEnabled: boolean;
    musicVolume: number;
    currentTrack: number;
  }>) => void;
}

export function SettingsModal({
  isOpen,
  onClose,
  onSave,
  onReturnToMenu,
  isSaving,
  saveSuccess,
  phasePreferences,
  onPhasePreferenceChange,
  fireWarningEnabled = true,
  onFireWarningChange,
  onRestartTutorial,
  audioSettings = { musicEnabled: true, musicVolume: 0.5, currentTrack: 0 },
  onAudioSettingsChange
}: SettingsModalProps) {
  const devModeSettings = useDevModeStore(state => state.settings);
  const toggleDevMode = useDevModeStore(state => state.toggleDevMode);

  // Navigation mode settings
  const navigationMode = useNavigationModeStore(state => state.mode);
  const setNavigationMode = useNavigationModeStore(state => state.setMode);
  const transitionPreferences = useSpatialStore(state => state.transitionPreferences);
  const updateTransitionPreferences = useSpatialStore(state => state.updateTransitionPreferences);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      className="max-w-4xl"
      data-testid="settings-modal"
    >
      <div className="flex flex-col h-[80vh] -m-6">
        {/* Fixed Header */}
        <div className="flex items-center gap-2 px-6 py-4 border-b border-theme-border">
          <Settings className="w-5 h-5 text-theme-text-secondary" />
          <h2 className="text-lg font-semibold text-theme-text-primary">Settings</h2>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6">
          <div className="space-y-6 py-4">


            {/* Music Settings */}
            {/* TODO: temporarily disabled */}
            {/* <MusicSettings
              settings={audioSettings}
              onUpdate={(updates) => onAudioSettingsChange?.(updates)}
            /> */}

            {/* Theme Settings */}
            <ThemeSettings />

            {/* Tutorial Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-theme-text-secondary border-b border-theme-border pb-2">
                Tutorial Settings
              </h3>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="font-medium text-theme-text-primary">
                    Restart Tutorial
                  </label>
                  <p className="text-sm text-theme-text-muted mt-1">
                    Restart the interactive tutorial from the beginning
                  </p>
                </div>
                <div>
                  <Button
                    onClick={onRestartTutorial}
                    variant="secondary"
                    className="flex items-center gap-2"
                    disabled={!onRestartTutorial}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Restart Tutorial
                  </Button>
                </div>
              </div>
            </div>

            {/* Employee Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-theme-text-secondary border-b border-theme-border pb-2">
                Employee Settings
              </h3>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label htmlFor="fire-warning" className="font-medium text-theme-text-primary">
                    Show Termination Warning
                  </label>
                  <p className="text-sm text-theme-text-muted mt-1">
                    Display a confirmation dialog when firing employees
                  </p>
                </div>
                <div className="flex items-center">
                  <input
                    id="fire-warning"
                    type="checkbox"
                    checked={fireWarningEnabled}
                    onChange={(e) => onFireWarningChange?.(e.target.checked)}
                    className="h-4 w-4 text-theme-accent focus:ring-theme-accent border-theme-border rounded"
                  />
                </div>
              </div>
            </div>

            {/* Phase Progression Settings */}
            <PhaseProgressionSettings
              preferences={phasePreferences}
              onPreferenceChange={onPhasePreferenceChange}
            />

            {/* Navigation Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-theme-text-secondary border-b border-theme-border pb-2">
                Navigation Settings
              </h3>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="font-medium text-theme-text-primary">
                    Navigation Style
                  </label>
                  <p className="text-sm text-theme-text-muted mt-1">
                    Choose between 3D campus view or classic tab navigation
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setNavigationMode('spatial')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                      navigationMode === 'spatial'
                        ? 'bg-theme-accent text-white border-theme-accent'
                        : 'bg-theme-bg-tertiary text-theme-text-secondary border-theme-border hover:border-theme-accent'
                    }`}
                  >
                    <Map className="w-4 h-4" />
                    <span className="text-sm">Campus (3D)</span>
                  </button>
                  <button
                    onClick={() => setNavigationMode('classic')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                      navigationMode === 'classic'
                        ? 'bg-theme-accent text-white border-theme-accent'
                        : 'bg-theme-bg-tertiary text-theme-text-secondary border-theme-border hover:border-theme-accent'
                    }`}
                  >
                    <Grid className="w-4 h-4" />
                    <span className="text-sm">Classic (Tabs)</span>
                  </button>
                </div>
              </div>

              {navigationMode === 'spatial' && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <label htmlFor="transition-enabled" className="font-medium text-theme-text-primary">
                        Transition Animations
                      </label>
                      <p className="text-sm text-theme-text-muted mt-1">
                        Enable smooth animations when navigating between locations
                      </p>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="transition-enabled"
                        type="checkbox"
                        checked={transitionPreferences.enabled}
                        onChange={(e) => updateTransitionPreferences({ enabled: e.target.checked })}
                        className="h-4 w-4 text-theme-accent focus:ring-theme-accent border-theme-border rounded"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <label htmlFor="reduced-motion" className="font-medium text-theme-text-primary">
                        Reduce Motion
                      </label>
                      <p className="text-sm text-theme-text-muted mt-1">
                        Disable animations for accessibility
                      </p>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="reduced-motion"
                        type="checkbox"
                        checked={transitionPreferences.reducedMotion}
                        onChange={(e) => updateTransitionPreferences({ reducedMotion: e.target.checked })}
                        className="h-4 w-4 text-theme-accent focus:ring-theme-accent border-theme-border rounded"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Developer Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-theme-text-secondary border-b border-theme-border pb-2">
                Developer Settings
              </h3>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label htmlFor="dev-mode" className="font-medium text-theme-text-primary">
                    Enable Developer Mode
                  </label>
                  <p className="text-sm text-theme-text-muted mt-1">
                    Show developer tools and debugging information (Press F12 to toggle)
                  </p>
                </div>
                <div className="flex items-center">
                  <input
                    id="dev-mode"
                    type="checkbox"
                    checked={devModeSettings.enabled}
                    onChange={() => toggleDevMode()}
                    className="h-4 w-4 text-theme-accent focus:ring-theme-accent border-theme-border rounded"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Fixed Footer with all buttons */}
        <div className="px-6 py-4 border-t border-theme-border space-y-3">
          <Button
            onClick={onSave}
            disabled={isSaving}
            className={`w-full flex items-center justify-center gap-2 ${saveSuccess
              ? 'bg-theme-accent hover:bg-theme-accent text-white'
              : 'bg-theme-accent hover:bg-theme-accent text-white'
              }`}
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Game'}
          </Button>

          <Button
            onClick={onReturnToMenu}
            variant="danger"
            className="w-full flex items-center justify-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Return to Menu
          </Button>

          <Button
            onClick={onClose}
            variant="secondary"
            className="w-full"
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}